import hljs from "highlight.js";
import remarkGfm from "remark-gfm";
import {
  createMemo,
  type Component,
  type JSX,
  Match,
  Show,
  Switch,
  onMount,
  createContext,
  useContext,
  For,
} from "solid-js";
import type { DOMElement } from "solid-js/jsx-runtime";
import { SolidMarkdown } from "solid-markdown";
import { twMerge } from "tailwind-merge";
import { CopyButton } from "@core/components/_original/CopyButton";
import { preprocessCiteTags } from "@core/domains/threads/utils/citationUtils";
import { ThreadTimelineUnit } from "../ThreadTimelineUnit";
import { SecondaryCTA } from "@core/components/cta/SecondaryCTA";
import { parseMarkdownToSections, type Section } from "../../utils/markdownSectionParser";
import { useWire } from "@core/wire";
import { urls } from "@core/lib/urls";
import { useUIState } from "@core/ui/UIState";
import { A } from "@solidjs/router";

const getDayOfWeek = () => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return days[new Date().getDay()] as string;
};
const getTimeOfDay = () => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return "morning";
  }
  if (hour >= 12 && hour < 17) {
    return "afternoon";
  }
  if (hour >= 17 && hour < 21) {
    return "evening";
  }
  return "afternoon";
};

const CodeBlockContext = createContext<boolean>(false);

export const MarkdownRenderer: Component<{
  md: string;
  prefix: string;
  /**
   * if markdown blocks can be highlighted by hovering
   * @default true
   */
  highlight?: boolean;
  includeURL?: {
    text: string;
    url: string;
  };
  onMouseOverChild?: (
    e: MouseEvent & {
      currentTarget: HTMLElement;
      target: DOMElement;
    },
  ) => void;
  onMouseOutChild?: (
    e: MouseEvent & {
      currentTarget: HTMLElement;
      target: DOMElement;
    },
  ) => void;
  onCiteClick?: (data: {
    chunkId: string;
    searchIdx: string;
    sourceName: string;
    displayNumber: number;
    messageId: string;
  }) => void;
  showCitations: boolean;
  citationNumberMap?: Map<string, number>;
}> = (props) => {
  const getKey = (index = "") => {
    return `${props.prefix};${index}`;
  };

  const wire = useWire();
  const ui = useUIState();

  const selectableProps = (): JSX.HTMLAttributes<HTMLElement> => {
    if (wire.isExtension()) return {};

    return {
      ...((props.highlight ?? true) ? { "data-selectable": true } : undefined),
      onMouseOver: (e) => {
        props.onMouseOverChild?.(e);
      },
      onMouseOut: (e) => {
        props.onMouseOutChild?.(e);
      },
    };
  };

  const clickableCiteStyle = {
    display: "inline-flex",
    userSelect: "none" as const,
    textDecoration: "none",
  };

  const nonClickableCiteStyle = {
    display: "inline-flex",
    userSelect: "none" as const,
  };

  const memoizedMarkdownData = createMemo(() => {
    const localCitationMap = new Map<
      string,
      {
        displayNumber: number;
        parsedCite: { chunkId: string; searchIdx: string; sourceName: string };
      }
    >();
    const uniqueAssetIdToDisplayNumberMap = new Map<string, number>();
    let uniqueCiteCounter = 0;
    // Create unique keys for each cite instance
    let citeInstanceIndex = 0;

    // Start with the raw markdown, apply variable replacements
    let processedMd = props.md
      .replaceAll("${{CurrentDayOfWeek}}", getDayOfWeek())
      .replaceAll("${{CurrentTimeOfDayGreeting}}", getTimeOfDay());

    // Apply preprocessing to handle malformed cite tags
    processedMd = preprocessCiteTags(processedMd);

    // Regex to find <cite>chunkID</cite>,
    // allowing for additional whitespace around chunkID that the LLM might add
    const citeRegex = /<cite>\s*([^<]*?)\s*<\/cite>/g;
    processedMd = processedMd.replace(citeRegex, (_match, capturedAssetIdRaw) => {
      if (!props.showCitations) {
        return ""; // If citations are hidden, replace <cite> tag with nothing
      }

      const capturedAssetId = capturedAssetIdRaw.trim();

      if (!capturedAssetId) {
        return "";
      }

      // chunkId is the chunk ID (e.g. "asset_d0f6rj4dib292g8p8pvg_cxp_47")
      const chunkId = capturedAssetId;
      // This will be used in the 'title' attribute of the rendered citation link.
      const sourceName = capturedAssetId;
      let searchIdx = "";

      // Attempt to extract searchIdx from a pattern like "_cxp_13" at the end of chunkId
      const searchIdxMatch = chunkId.match(/_cxp_([^_]+)$/);
      if (searchIdxMatch?.[1]) {
        searchIdx = searchIdxMatch[1];
      }

      const parsedCite = { chunkId, searchIdx, sourceName };

      let displayNumber: number;
      if (props.citationNumberMap?.has(chunkId)) {
        displayNumber = props.citationNumberMap.get(chunkId)!;
      } else {
        // Fallback to internal numbering if not in provided map or map not provided
        if (uniqueAssetIdToDisplayNumberMap.has(chunkId)) {
          displayNumber = uniqueAssetIdToDisplayNumberMap.get(chunkId)!;
        } else {
          uniqueCiteCounter++;
          displayNumber = uniqueCiteCounter;
          uniqueAssetIdToDisplayNumberMap.set(chunkId, displayNumber);
        }
      }

      citeInstanceIndex++;
      // Each instance needs a unique key for the markdown link that rehype will process,
      // even if they share the same display number.
      const urnKey = `urn:cite-instance:${citeInstanceIndex}`;

      localCitationMap.set(urnKey, { displayNumber, parsedCite });
      // The text part of the link is the displayNumber (which is now unique per chunkId)
      // The href part of the link is the urnKey (which is unique per instance)
      return `[${displayNumber}](${urnKey})`;
    });

    return { processedMd, citationMap: localCitationMap };
  });

  const sections = createMemo(() => {
    const currentMarkdownData = memoizedMarkdownData();
    const parsed = parseMarkdownToSections(currentMarkdownData.processedMd);
    return parsed;
  });

  return (
    <For each={sections()}>
      {(section: Section) => {
        const normalizedSectionName = section.name.toLowerCase();
        const isAnswer = normalizedSectionName === "answer";

        let timelineSectionType: "answer" | "reasoning" | "processing" = "answer";
        if (normalizedSectionName === "reasoning") {
          timelineSectionType = "reasoning";
        } else if (normalizedSectionName === "processing") {
          timelineSectionType = "processing";
        }

        return (
          <ThreadTimelineUnit
            tagLabel={section.name}
            includeURL={props.includeURL}
            muted={!isAnswer}
            section={timelineSectionType}
          >
            <div class={isAnswer ? "text-on-background" : "opacity-70"}>
              <SolidMarkdown
                remarkPlugins={[remarkGfm]}
                class="leading-relaxed rich-text"
                components={{
                  hr: () => <hr class="my-4" />,
                  h1: (props) => (
                    <p
                      data-block={getKey((props as unknown as { key: string }).key)}
                      class="text-4xl leading-normal mb-4 mt-6 scroll-mt-32 break-words"
                      // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
                      tabIndex={0}
                      {...selectableProps}
                    >
                      <strong class="font-semibold">{props.children}</strong>
                    </p>
                  ),
                  h2: (props) => (
                    <p
                      data-block={getKey((props as unknown as { key: string }).key)}
                      class="text-3xl leading-normal mb-4 mt-6 scroll-mt-32 break-words"
                      // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
                      tabIndex={0}
                      {...selectableProps}
                    >
                      <strong class="font-semibold">{props.children}</strong>
                    </p>
                  ),
                  h3: (props) => (
                    <p
                      data-block={getKey((props as unknown as { key: string }).key)}
                      class="text-2xl leading-normal mb-4 mt-6 scroll-mt-32 break-words"
                      // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
                      tabIndex={0}
                      {...selectableProps}
                    >
                      <strong class="font-semibold">{props.children}</strong>
                    </p>
                  ),
                  code: (props) => (
                    <CodeBlockContext.Provider value={true}>
                      <code>{props.children}</code>
                    </CodeBlockContext.Provider>
                  ),
                  text: (props) => {
                    const text = props.node.value;
                    const isInsideCodeBlock = useContext(CodeBlockContext) || false;

                    // Don't strip HTML tags if we're inside a code block - preserve them for display
                    if (isInsideCodeBlock) {
                      return text;
                    }

                    // Strip HTML tags for regular text content
                    return text ? text.replace(/<[^\/][^>]*>|<\/[^>]*>/g, "") : text;
                  },
                  p: (props) => (
                    <p
                      data-block={getKey((props as unknown as { key: string }).key)}
                      class="mb-1 leading-normal text-base scroll-mt-32 break-words"
                      // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
                      tabIndex={0}
                      {...selectableProps}
                    >
                      {props.children}
                    </p>
                  ),
                  ol: (props) => (
                    <ol
                      data-block={getKey((props as unknown as { key: string }).key)}
                      start={props.start}
                      class="list-outside list-decimal ml-4 sm:ml-6 mt-1 mb-4 scroll-mt-32 break-words"
                    >
                      {props.children}
                    </ol>
                  ),
                  ul: (props) => (
                    <ul
                      data-block={getKey((props as unknown as { key: string }).key)}
                      class="list-outside list-disc ml-4 sm:ml-6 mt-1 mb-4 scroll-mt-32 break-words"
                    >
                      {props.children}
                    </ul>
                  ),
                  li: (props) => (
                    <li
                      class="ml-2 mt-2 scroll-mt-32 break-words list-outside list-disc"
                      // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
                      tabIndex={0}
                      {...selectableProps}
                      data-block={getKey((props as unknown as { key: string }).key)}
                    >
                      {props.children}
                    </li>
                  ),
                  img: (props) => (
                    <Switch
                      fallback={
                        <div
                          data-block={getKey((props as unknown as { key: string }).key)}
                          class="flex justify-center my-4"
                        >
                          <img src={props.src} alt={props.alt} />
                        </div>
                      }
                    >
                      <Match when={props.src?.includes("example.com") || props.src?.includes("placeholder.com")}>
                        <div
                          data-block={getKey((props as unknown as { key: string }).key)}
                          class="my-8 p-4 max-w-48 mx-auto border-2 border-black dark:border-slate-200"
                        >
                          <svg
                            class="fill-black dark:fill-slate-100"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 64 64"
                          >
                            <title>Image not available</title>
                            <desc>Icon of a landscape with supporting text "Example"</desc>
                            <path d="M14.857,40.368c-2.525,0-4.579-2.033-4.579-4.532V11.093c0-2.499,2.054-4.532,4.579-4.532h34.366c2.525,0,4.579,2.033,4.579,4.532v24.742c0,2.499-2.054,4.532-4.579,4.532H14.857ZM25.795,20.666c-.869,0-1.701.333-2.283.915l-10.322,10.209v4.047c0,.91.748,1.651,1.668,1.651h34.366c.92,0,1.668-.74,1.668-1.651v-4.043l-5.632-5.577c-.604-.601-1.415-.932-2.282-.932s-1.681.332-2.291.935l-1.9,1.884,2.095,2.073c.563.561.562,1.476,0,2.039-.275.271-.641.421-1.031.421s-.756-.15-1.032-.423l-10.743-10.633c-.58-.58-1.412-.914-2.281-.914ZM14.857,9.443c-.92,0-1.668.74-1.668,1.65v16.626l8.266-8.178c1.101-1.093,2.681-1.721,4.338-1.721s3.241.627,4.346,1.721l6.587,6.521,1.902-1.884c1.158-1.144,2.703-1.775,4.348-1.775s3.185.63,4.341,1.772l3.574,3.537V11.093c0-.91-.748-1.65-1.668-1.65H14.857Z" />
                            <path d="M36.904,18.447c-1.233,0-2.236-.994-2.236-2.215s1.003-2.212,2.236-2.212,2.236.992,2.236,2.212-1.003,2.215-2.236,2.215Z" />
                            <path d="M10.04,47.471h4.842v.898h-3.772v2.607h3.183v.895h-3.183v3.025h3.902v.895h-4.972v-8.319Z" />
                            <path d="M17.45,52.576l-1.884-2.945h1.142l.834,1.35c.19.339.402.688.61,1.026h.05c.187-.339.375-.688.562-1.026l.762-1.35h1.097l-1.884,3.054,2.034,3.106h-1.142l-.917-1.432c-.215-.371-.445-.752-.677-1.105h-.05c-.215.354-.417.73-.63,1.105l-.844,1.432h-1.097l2.034-3.215Z" />
                            <path d="M21.477,54.19c0-1.34,1.169-2.013,3.822-2.297-.002-.794-.267-1.558-1.284-1.558-.72,0-1.369.339-1.856.663l-.412-.715c.572-.373,1.447-.806,2.443-.806,1.519,0,2.164.999,2.164,2.535v3.779h-.872l-.087-.737h-.035c-.595.49-1.287.888-2.059.888-1.042,0-1.824-.638-1.824-1.751ZM25.299,54.287v-1.711c-2.086.25-2.791.754-2.791,1.543,0,.7.48.984,1.092.984.6,0,1.097-.289,1.699-.816Z" />
                            <path d="M28.307,49.63h.872l.087.893h.035c.545-.584,1.197-1.046,1.936-1.046.952,0,1.462.453,1.719,1.175.647-.692,1.297-1.175,2.054-1.175,1.279,0,1.894.838,1.894,2.409v3.905h-1.054v-3.769c0-1.147-.367-1.64-1.157-1.64-.485,0-.984.314-1.561.947v4.461h-1.052v-3.769c0-1.147-.367-1.64-1.159-1.64-.465,0-.984.314-1.559.947v4.461h-1.054v-6.16Z" />
                            <path d="M38.913,49.63h.872l.087.71h.035c.562-.462,1.264-.863,1.984-.863,1.609,0,2.468,1.237,2.468,3.141,0,2.1-1.274,3.324-2.696,3.324-.57,0-1.154-.26-1.724-.712l.027,1.076v2.085h-1.054v-8.759ZM43.272,52.625c0-1.358-.47-2.27-1.641-2.27-.527,0-1.059.287-1.664.841v3.23c.562.467,1.104.641,1.514.641,1.032,0,1.791-.92,1.791-2.441Z" />
                            <path d="M45.96,54.554v-7.792h1.054v7.867c0,.319.14.448.292.448.062,0,.112,0,.232-.027l.142.796c-.15.054-.33.096-.607.096-.789,0-1.114-.497-1.114-1.387Z" />
                            <path d="M48.758,52.719c0-2.006,1.367-3.242,2.798-3.242,1.589,0,2.483,1.13,2.483,2.891,0,.22-.017.44-.047.594h-4.192c.075,1.303.882,2.152,2.069,2.152.597,0,1.087-.193,1.549-.492l.377.683c-.547.354-1.209.638-2.056.638-1.659,0-2.98-1.199-2.98-3.222ZM53.111,52.215c0-1.234-.562-1.907-1.534-1.907-.877,0-1.659.695-1.786,1.907h3.32Z" />
                          </svg>
                        </div>
                      </Match>
                    </Switch>
                  ),
                  a: (linkProps) => {
                    const href = linkProps.href;

                    // Expands any dynamic variables sent from the BE
                    const expandedHref = () => {
                      const decoded = decodeURIComponent(href || "");
                      if (decoded === "{_stx_collection}" || decoded === "{_stx_assets_status}")
                        return { url: urls.collection(ui.collectionId[0]()), local: true };
                      if (decoded === "{_stx_change_scope}")
                        return { url: "https://docs.storytell.ai/features/knowledge/manage-knowledge", local: false };
                      return { url: href, local: false };
                    };
                    return (
                      <Show
                        when={href?.startsWith("urn:cite-instance:")}
                        fallback={
                          <A
                            data-block={getKey((linkProps as unknown as { key: string }).key)}
                            class="text-violet-500 dark:text-violet-400 underline leading-normal text-base underline-offset-2 scroll-mt-32 break-words"
                            href={expandedHref().url || "#"}
                            target={expandedHref().local ? "_self" : "_blank"}
                            rel={expandedHref().local ? undefined : "noreferrer"}
                          >
                            {linkProps.children}
                          </A>
                        }
                      >
                        <Show
                          when={memoizedMarkdownData().citationMap.get(href!)}
                          fallback={
                            <span class="text-red-500 font-bold" title={`Invalid citation link: ${href}`}>
                              [Invalid Cite Ref]
                            </span>
                          }
                        >
                          {(citeMeta) => {
                            // Check if the chunkId exists in the citation map
                            const isValidReference = () =>
                              props.citationNumberMap?.has(citeMeta().parsedCite.chunkId) ?? true;

                            return (
                              <Show
                                when={props.onCiteClick && isValidReference()}
                                fallback={
                                  <Show
                                    when={isValidReference()}
                                    fallback={
                                      <span
                                        class="citation-badge rounded-sm bg-gray-300 text-gray-500 text-[0.75rem] font-semibold flex items-center justify-center px-1 ml-1"
                                        style={nonClickableCiteStyle}
                                        title={`Reference ${citeMeta().displayNumber}: ${citeMeta().parsedCite.sourceName} (Reference not found)`}
                                      >
                                        {citeMeta().displayNumber}
                                      </span>
                                    }
                                  >
                                    <span
                                      class="citation-badge rounded-sm bg-gray-400 text-white text-[0.75rem] font-semibold flex items-center justify-center px-1 ml-1"
                                      style={nonClickableCiteStyle}
                                      title={`Reference ${citeMeta().displayNumber}: ${citeMeta().parsedCite.sourceName}`}
                                    >
                                      {citeMeta().displayNumber}
                                    </span>
                                  </Show>
                                }
                              >
                                <a
                                  class="citation-badge rounded-sm bg-violet-500 text-white text-[0.75rem] font-semibold flex items-center justify-center px-1 ml-1 cursor-pointer"
                                  style={clickableCiteStyle}
                                  // biome-ignore lint/a11y/useValidAnchor: <explanation>
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    props.onCiteClick?.({
                                      ...citeMeta().parsedCite,
                                      displayNumber: citeMeta().displayNumber,
                                      messageId: props.prefix,
                                    });
                                  }}
                                  title={`Reference ${citeMeta().displayNumber}: ${citeMeta().parsedCite.sourceName}`}
                                >
                                  {citeMeta().displayNumber}
                                </a>
                              </Show>
                            );
                          }}
                        </Show>
                      </Show>
                    );
                  },
                  pre: (props) => {
                    let ref!: HTMLPreElement;

                    const languageClass = () => {
                      if (Array.isArray(props.node.properties?.className)) {
                        const className = props.node.properties.className
                          .find((c) => c.toString().startsWith("language"))
                          ?.toString();
                        if (className) return className;
                      }
                      const code = props.node.children.find((c) => c.type === "element" && c.tagName === "code");
                      if (code && "properties" in code && Array.isArray(code.properties?.className)) {
                        const className = code.properties.className
                          .find((c) => c.toString().startsWith("language"))
                          ?.toString();
                        if (className) return className;
                      }
                    };
                    onMount(() => {
                      hljs.highlightElement(ref);
                    });
                    return (
                      <CodeBlockContext.Provider value={true}>
                        <div class="relative rounded border border-indigo-900 mb-4">
                          <div class="absolute right-0 top-0 rounded-t flex justify-end bg-indigo-1000 dark:bg-indigo-1100/70 pr-1 pt-1">
                            <CopyButton
                              class="bg-white dark:bg-indigo-800 text-base leading-normal"
                              content=""
                              label="Copy"
                              onClick={async () => {
                                await navigator.clipboard.writeText(ref.innerText);
                              }}
                            />
                          </div>
                          <pre
                            data-block={getKey((props as unknown as { key: string }).key)}
                            ref={ref}
                            class={twMerge(
                              "overflow-auto p-4 pr-11 rounded-b scroll-mt-32 text-base leading-normal",
                              props.class,
                              languageClass() || false,
                            )}
                          >
                            <code>{props.children}</code>
                          </pre>
                        </div>
                      </CodeBlockContext.Provider>
                    );
                  },
                  table: (props) => {
                    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                    const extractTextContent = (node: any): string => {
                      if (node.type === "text") return node.value || "";
                      if (node.type === "element" && node.children) {
                        return node.children.map(extractTextContent).join("");
                      }
                      return "";
                    };

                    const csv = () => {
                      const node = props.node;

                      const head = node.children.find((c) => c.type === "element" && c.tagName === "thead");
                      const body = node.children.find((c) => c.type === "element" && c.tagName === "tbody");

                      if (!head || !body) return null;
                      if (head.type !== "element" || body.type !== "element") return null;

                      const row = head.children.find((c) => c.type === "element" && c.tagName === "tr");
                      if (!row || row.type !== "element") return null;

                      const heads = row.children
                        .filter((c) => c.type === "element" && c.tagName === "th")
                        .map((c) => (c.type === "element" ? extractTextContent(c) : ""));

                      const rows = body.children
                        .filter((c) => c.type === "element" && c.tagName === "tr")
                        .map((c) =>
                          c.type === "element"
                            ? c.children
                                .filter((c) => c.type === "element" && c.tagName === "td")
                                .map((c) => extractTextContent(c))
                            : undefined,
                        )
                        .filter(Boolean);

                      return [heads, ...rows] as string[][];
                    };

                    const downloadCsv = () => {
                      const csvData = csv();
                      if (!csvData) return;

                      // Convert array of arrays to CSV string
                      const csvContent = csvData
                        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
                        .join("\n");

                      // Create a blob and download link
                      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.setAttribute("href", url);
                      link.setAttribute("download", "data.csv");
                      link.style.display = "none";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    };

                    return (
                      <div
                        data-block={getKey((props as unknown as { key: string }).key)}
                        class="max-w-full overflow-x-auto border border-slate-100 dark:border-indigo-900 rounded bg-white/30 dark:text-white dark:bg-indigo-1100/70 mt-4 mb-6 scroll-mt-32"
                      >
                        <table class="w-full table-automin-w-full divide-y divide-slate-100 dark:divide-indigo-900">
                          {props.children}
                        </table>

                        <div class="flex justify-end border-t border-slate-100 dark:border-indigo-900 py-1 px-2">
                          <SecondaryCTA
                            data-test-id="download-csv"
                            size="small"
                            onClick={downloadCsv}
                            label="Download as CSV"
                            accessiblePrefix="Download as CSV"
                          />
                        </div>
                      </div>
                    );
                  },
                  th: (props) => (
                    <th
                      data-block={getKey((props as unknown as { key: string }).key)}
                      class="p-2 text-left text-sm leading-normal font-semibold text-slate-900 dark:text-white scroll-mt-32"
                    >
                      {props.children}
                    </th>
                  ),
                  tbody: (props) => (
                    <tbody
                      data-block={getKey((props as unknown as { key: string }).key)}
                      class="divide-y divide-slate-100 dark:divide-indigo-900 scroll-mt-32"
                    >
                      {props.children}
                    </tbody>
                  ),
                  td: (props) => (
                    <td
                      data-block={getKey((props as unknown as { key: string }).key)}
                      class="whitespace-nowrap px-3 py-4 text-sm leading-normal text-slate-800 dark:text-indigo-100 scroll-mt-32"
                    >
                      {props.children}
                    </td>
                  ),
                }}
              >
                {section.content}
              </SolidMarkdown>
            </div>
          </ThreadTimelineUnit>
        );
      }}
    </For>
  );
};
